// Client-side EMI helpers. We compute in the browser rather than calling
// PayU's API because:
//   • PayU's `/calculateEmi/v2` requires an auth header (x-credential-username)
//     — no free tier for arbitrary marketplaces.
//   • The formula is public + simple. Zero network latency, works offline.
//
// Used-car loan defaults calibrated to what Rupyy/CARS24/Shriram publish
// on their EMI calculators (as of Feb 2026):
//   • Down payment: 20% of vehicle price
//   • Interest rate: 14% p.a. (used-car rates run 3–4 pp higher than new)
//   • Tenure: 60 months
//   • Processing fee: 1% (informational, not added to EMI)

export const EMI_DEFAULTS = {
  downPaymentPct: 20,       // % of asking price
  interestRate: 14,         // annual %
  tenureMonths: 60,         // 5 years
  processingFeePct: 1,      // % of principal (informational)
  minTenure: 12,
  maxTenure: 84,            // 7 yrs — Rupyy cap for used cars
  minDownPaymentPct: 10,
  maxDownPaymentPct: 60,
  minRate: 10,
  maxRate: 20,
};

/**
 * Standard reducing-balance EMI:
 *   E = P × r × (1+r)^n / ((1+r)^n − 1)
 *
 * Returns 0 for invalid / zero-principal inputs so consumers can render
 * "N/A" without extra branching.
 */
export function calcEmi({ principal, annualRatePct, tenureMonths }) {
  const P = Number(principal) || 0;
  const nRaw = Number(tenureMonths) || 0;
  const rAnnual = Number(annualRatePct) || 0;
  if (P <= 0 || nRaw <= 0) return 0;
  const r = rAnnual / 12 / 100;
  if (r === 0) return P / nRaw;
  const pow = Math.pow(1 + r, nRaw);
  return Math.round((P * r * pow) / (pow - 1));
}

/**
 * Convenience wrapper that takes the vehicle asking price + down-payment %
 * and returns the monthly EMI. Used on listing cards where we don't render
 * the full breakdown — just the "from ₹X,XXX/month" hero.
 */
export function estimateMonthly(askingPrice, opts = {}) {
  const {
    downPaymentPct = EMI_DEFAULTS.downPaymentPct,
    interestRate  = EMI_DEFAULTS.interestRate,
    tenureMonths  = EMI_DEFAULTS.tenureMonths,
  } = opts;
  const dp = (askingPrice || 0) * (downPaymentPct / 100);
  return calcEmi({
    principal: (askingPrice || 0) - dp,
    annualRatePct: interestRate,
    tenureMonths,
  });
}

export const fmtInr = (v) =>
  (v || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 });
